import dealerService, {
  Dealer,
  DealerListParams,
  DealerListResponse,
  UpdateDealerBrandingRequest,
  UpdateDealerDomainRequest,
} from "@/services/dealerService";

const STORAGE_KEY = "dealer-branding";

const loadBrandingFromStorage = (): Dealer | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

type State = {
  dealers: Dealer[];
  currentDealer: Dealer | null;
  myDealer: Dealer | null;
  total: number;
  loading: boolean;
  isLoadingMyDealer: boolean;
};

const state: State = {
  dealers: [],
  currentDealer: null,
  myDealer: loadBrandingFromStorage(),
  total: 0,
  loading: false,
  isLoadingMyDealer: false,
};

const mutations = {
  setDealers(state: State, dealers: Dealer[]) {
    state.dealers = dealers;
  },
  setCurrentDealer(state: State, dealer: Dealer | null) {
    state.currentDealer = dealer;
  },
  setMyDealer(state: State, dealer: Dealer | null) {
    state.myDealer = dealer;
    if (dealer) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dealer));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  },
  setTotal(state: State, total: number) {
    state.total = total;
  },
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setLoadingMyDealer(state: State, loading: boolean) {
    state.isLoadingMyDealer = loading;
  },
};

const getters = {
  dealers: (state: State) => state.dealers,
  currentDealer: (state: State) => state.currentDealer,
  myDealer: (state: State) => state.myDealer,
  total: (state: State) => state.total,
  loading: (state: State) => state.loading,
};

const actions = {
  async loadDealers({ commit }: { commit: any }, params: DealerListParams = {}) {
    try {
      commit("setLoading", true);
      const response: DealerListResponse = await dealerService.listDealers(params);
      commit("setDealers", response.items);
      commit("setTotal", response.total);
      return response.items;
    } catch (error) {
      commit("setDealers", []);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async loadDealerById({ commit }: { commit: any }, dealerId: string) {
    try {
      commit("setLoading", true);
      const dealer = await dealerService.getDealerById(dealerId);
      commit("setCurrentDealer", dealer);
      return dealer;
    } catch (error) {
      commit("setCurrentDealer", null);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async createDealer(_: any, dealerData: any) {
    return dealerService.createDealer(dealerData);
  },

  async updateDealer(_: any, { dealerId, data }: { dealerId: string; data: any }) {
    return dealerService.updateDealer(dealerId, data);
  },

  async deleteDealer(_: any, dealerId: string) {
    return dealerService.deleteDealer(dealerId);
  },

  async loadMyDealer({ commit, state }: { commit: any; state: State }, forceRefresh = false) {
    // Evitar múltiples llamadas simultáneas
    if (state.isLoadingMyDealer) {
      return state.myDealer;
    }
    // Si ya hay data y no se fuerza refresh, usar cache
    if (state.myDealer && !forceRefresh) {
      return state.myDealer;
    }
    try {
      commit("setLoadingMyDealer", true);
      const dealer = await dealerService.getMyDealer();
      commit("setMyDealer", dealer);
      return dealer;
    } finally {
      commit("setLoadingMyDealer", false);
    }
  },

  // Admin: PUT /dealers/{id}/branding
  async updateDealerBranding({ commit }: { commit: any }, { dealerId, data }: { dealerId: string; data: UpdateDealerBrandingRequest }) {
    const dealer = await dealerService.updateDealerBranding(dealerId, data);
    commit("setCurrentDealer", dealer);
    return dealer;
  },

  // DealerAdmin: PUT /dealers/me/branding
  async updateMyBranding({ commit }: { commit: any }, data: UpdateDealerBrandingRequest) {
    const dealer = await dealerService.updateMyBranding(data);
    commit("setMyDealer", dealer);
    return dealer;
  },

  // Admin: POST /dealers/{id}/logo
  async uploadDealerLogo({ commit }: { commit: any }, { dealerId, file }: { dealerId: string; file: File }) {
    const dealer = await dealerService.uploadDealerLogo(dealerId, file);
    commit("setCurrentDealer", dealer);
    return dealer;
  },

  // DealerAdmin: POST /dealers/me/logo
  async uploadMyLogo({ commit }: { commit: any }, file: File) {
    const dealer = await dealerService.uploadMyLogo(file);
    commit("setMyDealer", dealer);
    return dealer;
  },

  // Admin: DELETE /dealers/{id}/logo
  async deleteDealerLogo({ commit }: { commit: any }, dealerId: string) {
    const dealer = await dealerService.deleteDealerLogo(dealerId);
    commit("setCurrentDealer", dealer);
    return dealer;
  },

  // DealerAdmin: DELETE /dealers/me/logo
  async deleteMyLogo({ commit }: { commit: any }) {
    const dealer = await dealerService.deleteMyLogo();
    commit("setMyDealer", dealer);
    return dealer;
  },

  // Admin only: PUT /dealers/{id}/domain
  async updateDomain({ commit }: { commit: any }, { dealerId, data }: { dealerId: string; data: UpdateDealerDomainRequest }) {
    const dealer = await dealerService.updateDomain(dealerId, data);
    commit("setCurrentDealer", dealer);
    return dealer;
  },

  clearCurrentDealer({ commit }: { commit: any }) {
    commit("setCurrentDealer", null);
  },

  clearMyDealer({ commit }: { commit: any }) {
    commit("setMyDealer", null);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
